import type { OnsiteRun, OnsiteWorkspace, SftpUser } from "./types";

export const SFTP_USERS_ACTION = "sftp-users";

// Mirrors SFTP_USERNAME and SYSTEM_ACCOUNTS in apps/platform/src/features/onsite/onsiteSftpUsers.ts.
const SFTP_USERNAME = /^[a-z][a-z0-9_-]{2,31}$/;
const SYSTEM_ACCOUNTS = new Set([
  "root",
  "daemon",
  "bin",
  "sys",
  "sync",
  "games",
  "man",
  "mail",
  "news",
  "uucp",
  "proxy",
  "www-data",
  "backup",
  "list",
  "irc",
  "gnats",
  "nobody",
  "sshd",
  "messagebus",
  "admin",
  "ubuntu",
  "debian",
]);

const ACTION_LABELS: Record<string, string> = { [SFTP_USERS_ACTION]: "SFTP users" };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSftpUser(value: unknown): value is SftpUser {
  return (
    isRecord(value) &&
    typeof value.username === "string" &&
    (value.lastSetAt === null || typeof value.lastSetAt === "string")
  );
}

function usersFrom(value: unknown): SftpUser[] | null {
  return Array.isArray(value) ? value.filter(isSftpUser) : null;
}

/** Null when the name can be added, else why not. */
export function sftpUsernameError(name: string, existing: readonly string[]): string | null {
  if (!SFTP_USERNAME.test(name)) {
    return "Use 3–32 characters: lowercase letters, digits, '_' or '-', starting with a letter.";
  }
  if (SYSTEM_ACCOUNTS.has(name) || name.startsWith("systemd-")) {
    return `"${name}" is a system account on the SFTP server. Pick another name.`;
  }
  if (existing.includes(name)) return `"${name}" already exists.`;
  return null;
}

/** The users the last sftp-users run reported (`facts.sftpUsers`); null until one has. */
export function sftpUsersOf(workspace: OnsiteWorkspace): SftpUser[] | null {
  return isRecord(workspace.facts) ? usersFrom(workspace.facts.sftpUsers) : null;
}

/** An sftp-users run's reported users, or null for any other run or one with no result yet. */
export function runUsersOf(run: OnsiteRun): SftpUser[] | null {
  if (run.action !== SFTP_USERS_ACTION || !isRecord(run.result)) return null;
  return usersFrom(run.result.users);
}

export function describeUsers(users: SftpUser[]): string {
  return users.length > 0 ? `Users: ${users.map((u) => u.username).join(", ")}` : "No users";
}

/** An sftp-users run that names no user: the agent only read the secret. */
function isRefreshRun(run: OnsiteRun): boolean {
  if (run.action !== SFTP_USERS_ACTION || !isRecord(run.inputArgs)) return false;
  const { add, rotate, remove } = run.inputArgs;
  return [add, rotate, remove].every((list) => Array.isArray(list) && list.length === 0);
}

export function runActionLabel(run: OnsiteRun): string {
  if (isRefreshRun(run)) return "Refresh SFTP users";
  return ACTION_LABELS[run.action] ?? run.action;
}

export function sftpSecretName(workspace: OnsiteWorkspace): string {
  return `SimplicitySFTPUsers-${workspace.name}`;
}

export function sftpSecretUrl(workspace: OnsiteWorkspace): string {
  const secret = encodeURIComponent(sftpSecretName(workspace));
  const project = encodeURIComponent(workspace.gcpProjectId);
  return `https://console.cloud.google.com/security/secret-manager/secret/${secret}/versions?project=${project}`;
}
